import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { THEMES } from 'src/app/data/product/product.data';
import { Product } from 'src/app/model/product/product.model';
import { ThemeService } from 'src/app/service/theme/theme.service';

// Bridges the user's active Codigrate Chrome theme (surfaced by the "All In One
// Themes" extension's content script, see chrome-themes/all-in-one-themes/
// bridge.js) to the site. When the visitor has that extension installed and a
// theme selected, we OFFER to match the site skin to it. Nothing is applied
// without the user accepting the offer.
//
// The extension delivers the theme slug two ways: a `data-codigrate-chrome-theme`
// attribute on <html> (set at document_start) and a window.postMessage signal.
// We read both and also send a one-off request, so whichever arrives first wins.

const SIGNAL_SOURCE = 'codigrate-chrome-theme';
const REQUEST_SOURCE = 'codigrate-chrome-theme-request';
const DISMISSED_KEY = 'chromeThemeOfferDismissed';

@Injectable( { providedIn: 'root' } )
export class ChromeThemeBridgeService {

  private offeredThemeSubject = new BehaviorSubject<Product | null>( null );
  // The theme we are currently offering to apply (null when nothing to offer).
  offeredTheme: Observable<Product | null> = this.offeredThemeSubject.asObservable();

  private started = false;

  constructor( private themeService: ThemeService ) {
  }

  // Called once from the offer popup's ngOnInit (the popup lives at the app root,
  // so this runs on every page).
  start(): void {
    if ( this.started || typeof window === 'undefined' ) {
      return;
    }
    this.started = true;

    // 1) Slug already on <html> (extension ran at document_start).
    const fromAttr = document.documentElement.dataset[ 'codigrateChromeTheme' ];
    if ( fromAttr ) {
      this.consider( fromAttr );
    }

    // 2) Live signal (covers the case where the page booted before the extension
    //    published, or the user switches theme in the popup with the tab open).
    window.addEventListener( 'message', ( ev: MessageEvent ) => {
      if ( ev.source === window && ev.data && ev.data.source === SIGNAL_SOURCE && ev.data.theme ) {
        this.consider( ev.data.theme );
      }
    } );

    // 3) Ask the extension to (re)publish, in case we missed the initial post.
    window.postMessage( { source: REQUEST_SOURCE }, '*' );
  }

  // Apply the offered theme as a full IDE skin and clear the offer.
  accept(): void {
    const product = this.offeredThemeSubject.value;
    if ( product ) {
      this.themeService.setProductTheme( product );
    }
    this.offeredThemeSubject.next( null );
  }

  // Remember the user said no for THIS slug, so we do not nag again for it. A
  // different Chrome theme later still gets offered.
  dismiss(): void {
    const product = this.offeredThemeSubject.value;
    if ( product ) {
      try {
        localStorage.setItem( DISMISSED_KEY, product.key );
      } catch ( e ) {
        // storage unavailable, just skip persistence.
      }
    }
    this.offeredThemeSubject.next( null );
  }

  // Decide whether a detected slug is worth offering.
  private consider( slug: string ): void {
    if ( this.offeredThemeSubject.value ) {
      return; // already offering something
    }

    const product = this.findTheme( slug );
    if ( !product ) {
      return; // not one of our site themes
    }

    if ( this.isAlreadyActive( slug ) ) {
      return; // the site is already wearing this theme
    }

    if ( this.wasDismissed( slug ) ) {
      return; // user already declined this exact theme
    }

    // The offer toast stacks ABOVE the cookie bar (see theme-offer-popup), so it
    // can surface even before cookies are accepted.
    this.offeredThemeSubject.next( product );
  }

  private findTheme( slug: string ): Product | null {
    for ( const line of THEMES ) {
      const found = line.products.find( p => p.key === slug );
      if ( found ) {
        return found;
      }
    }
    return null;
  }

  private isAlreadyActive( slug: string ): boolean {
    try {
      const raw = localStorage.getItem( 'productTheme' ) || sessionStorage.getItem( 'productTheme' );
      if ( !raw ) {
        return false;
      }
      const parsed = JSON.parse( raw );
      return parsed && parsed.key === slug;
    } catch ( e ) {
      return false;
    }
  }

  private wasDismissed( slug: string ): boolean {
    try {
      return localStorage.getItem( DISMISSED_KEY ) === slug;
    } catch ( e ) {
      return false;
    }
  }
}
